import React, { useEffect, useState } from "react";
import Sheet from "@amzn/meridian/sheet";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Button from "@amzn/meridian/button";
import Checkbox from "@amzn/meridian/checkbox";
import Icon from "@amzn/meridian/icon";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import checkTokens from "@amzn/meridian-tokens/base/icon/check-large";
import minusTokens from "@amzn/meridian-tokens/base/icon/minus";
import ControlledExpander from "../activeProjects/common/ControlledExpander";
import { useBulkEditContext } from "../../store/bulk-edit-store";
import { FIELD_GROUP_BUILDING_ATTRIBUTES, FIELD_GROUP_PRODUCTIVITY, FIELD_GROUP_SITE_DETAILS, BULK_EDIT_OPTIONAL_IDENTIFIER_COLUMNS } from "./BulkEditConstants";
import cloneDeep from "lodash/cloneDeep";

interface BulkEditColumnSelectorProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export default function BulkEditColumnSelector({ isOpen, setIsOpen }: BulkEditColumnSelectorProps) {
  const { rows, visibleColumns, setVisibleColumns } = useBulkEditContext();
  const [localSelectedColumns, setLocalSelectedColumns] = useState<Set<string>>(new Set([]));

  // Get all editable columns grouped by category
  const allEditableColumns = rows.length > 0 ? Object.keys(rows[0].editableFields) : [];
  const siteDetailsColumns: string[] = [...BULK_EDIT_OPTIONAL_IDENTIFIER_COLUMNS];
  const buildingAttributeColumns = rows.length > 0
    ? allEditableColumns.filter((col) => rows[0].editableFields[col]?.fieldGroup === FIELD_GROUP_BUILDING_ATTRIBUTES)
    : [];
  const productivityColumns = rows.length > 0
    ? allEditableColumns.filter((col) => rows[0].editableFields[col]?.fieldGroup === FIELD_GROUP_PRODUCTIVITY)
    : [];

  useEffect(() => {
    setLocalSelectedColumns(new Set(visibleColumns));
  }, [visibleColumns, isOpen]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const toggleColumn = (columnName: string) => () => {
    const next = cloneDeep(localSelectedColumns);
    if (next.has(columnName)) {
      next.delete(columnName);
    } else {
      next.add(columnName);
    }
    setLocalSelectedColumns(next);
  };

  const resetToDefault = () => {
    setLocalSelectedColumns(new Set([...siteDetailsColumns, ...allEditableColumns]));
  };

  const saveSelection = () => {
    setVisibleColumns(Array.from(localSelectedColumns));
    setIsOpen(false);
  };

  const isGroupAllSelected = (groupColumns: string[]) => {
    return groupColumns.every((col) => localSelectedColumns.has(col));
  };

  const toggleGroupAll = (groupColumns: string[]) => () => {
    const allSelected = isGroupAllSelected(groupColumns);
    const next = cloneDeep(localSelectedColumns);
    for (const col of groupColumns) {
      if (allSelected) {
        next.delete(col);
      } else {
        next.add(col);
      }
    }
    setLocalSelectedColumns(next);
  };

  const getCheckIcon = (groupColumns: string[]) => {
    return (
      <Icon tokens={isGroupAllSelected(groupColumns) ? checkTokens : minusTokens} />
    );
  };

  return (
    <Sheet open={isOpen} onClose={handleClose} type="overlay">
      <Column>
        <Row>
          <Text type="h200">Column Filter</Text>
          <Button onClick={handleClose} type="icon">
            <Icon tokens={closeSmallTokens}>Close</Icon>
          </Button>
        </Row>
        <Row>
          <Button type="secondary" onClick={resetToDefault}>
            Reset to Default
          </Button>
          <Button minWidth="40%" onClick={saveSelection}>
            Save
          </Button>
        </Row>
        <Column spacingInset="200">
          {/* Site Details group (optional identifier columns) */}
          <ControlledExpander
            title={
              <Row>
                <Button type="icon" onClick={toggleGroupAll(siteDetailsColumns)}>
                  {getCheckIcon(siteDetailsColumns)}
                </Button>
                <Text>Site Details ({siteDetailsColumns.length})</Text>
              </Row>
            }
            type="list"
          >
            {siteDetailsColumns.map((col) => (
              <Checkbox
                key={`sd-${col}`}
                checked={localSelectedColumns.has(col)}
                onChange={toggleColumn(col)}
              >
                {col}
              </Checkbox>
            ))}
          </ControlledExpander>

          {/* Building Attributes group */}
          <ControlledExpander
            title={
              <Row>
                <Button type="icon" onClick={toggleGroupAll(buildingAttributeColumns)}>
                  {getCheckIcon(buildingAttributeColumns)}
                </Button>
                <Text>Building Attributes ({buildingAttributeColumns.length})</Text>
              </Row>
            }
            type="list"
          >
            {buildingAttributeColumns.map((col) => (
              <Checkbox
                key={`ba-${col}`}
                checked={localSelectedColumns.has(col)}
                onChange={toggleColumn(col)}
              >
                {col}
              </Checkbox>
            ))}
          </ControlledExpander>

          {/* Productivity group */}
          <ControlledExpander
            title={
              <Row>
                <Button type="icon" onClick={toggleGroupAll(productivityColumns)}>
                  {getCheckIcon(productivityColumns)}
                </Button>
                <Text>Productivity ({productivityColumns.length})</Text>
              </Row>
            }
            type="list"
          >
            {productivityColumns.map((col) => (
              <Checkbox
                key={`prod-${col}`}
                checked={localSelectedColumns.has(col)}
                onChange={toggleColumn(col)}
              >
                {col}
              </Checkbox>
            ))}
          </ControlledExpander>
        </Column>
      </Column>
    </Sheet>
  );
}
